import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Send, Sparkles, Loader2, Trash2, Code2 } from 'lucide-react';

export const ChatPane = ({ 
  messages = [], 
  isLoading = false, 
  onSendMessage, 
  onClear,
  currentCode = '',
}) => {
  const [input, setInput] = useState('');
  const [includeCode, setIncludeCode] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      const scrollElement = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollElement) {
        scrollElement.scrollTop = scrollElement.scrollHeight;
      }
    }
  }, [messages]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (input.trim() && !isLoading) {
      onSendMessage?.(input, includeCode ? currentCode : null);
      setInput('');
      setIncludeCode(false);
    }
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('he-IL', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Parse code blocks in messages
  const renderMessageContent = (content) => {
    const parts = content.split(/(```[\s\S]*?```)/g);
    
    return parts.map((part, index) => {
      if (part.startsWith('```')) {
        const codeContent = part.replace(/```\w*\n?/g, '').replace(/```$/g, '');
        return (
          <pre 
            key={index} 
            className="bg-editor-bg rounded-lg p-3 my-2 overflow-x-auto text-sm font-mono text-terminal-text border border-border/20"
            dir="ltr"
          >
            <code>{codeContent}</code>
          </pre>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  return (
    <div className="flex flex-col h-full bg-card rounded-xl overflow-hidden border border-border/30 shadow-md">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-l from-primary/10 to-accent/10 border-b border-border/20">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-glow">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-success rounded-full border-2 border-card" />
          </div>
          <div>
            <h3 className="font-bold text-foreground">החונך שלי</h3>
            <p className="text-xs text-muted-foreground">My Mentor - מוכן לעזור!</p>
          </div>
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={onClear}
          className="text-muted-foreground hover:text-foreground"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      {/* Messages */}
      <ScrollArea ref={scrollRef} className="flex-1 p-4">
        <div className="space-y-4">
          {messages.map((message) => (
            <div 
              key={message.id}
              className={`flex gap-3 ${message.role === 'user' ? 'flex-row-reverse' : ''} animate-slide-up`}
            >
              {/* Avatar */}
              <Avatar className="h-8 w-8 shrink-0">
                <AvatarFallback 
                  className={`text-xs font-bold ${
                    message.role === 'user' 
                      ? 'bg-secondary text-secondary-foreground' 
                      : 'bg-gradient-to-br from-primary to-accent text-white'
                  }`}
                >
                  {message.role === 'user' ? '👤' : '🐍'}
                </AvatarFallback>
              </Avatar>
              
              {/* Message bubble */}
              <div 
                className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                  message.role === 'user'
                    ? 'bg-secondary text-secondary-foreground rounded-tr-sm'
                    : 'bg-muted text-foreground rounded-tl-sm'
                }`}
              >
                <div className="text-sm leading-relaxed whitespace-pre-wrap">
                  {renderMessageContent(message.content)}
                </div>
                <div className={`text-xs mt-2 ${
                  message.role === 'user' ? 'text-secondary-foreground/60' : 'text-muted-foreground'
                }`}>
                  {formatTime(message.timestamp)}
                </div>
              </div>
            </div>
          ))}
          
          {/* Typing indicator */}
          {isLoading && (
            <div className="flex gap-3 animate-slide-up">
              <Avatar className="h-8 w-8 shrink-0">
                <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white text-xs">
                  🐍
                </AvatarFallback>
              </Avatar>
              <div className="bg-muted rounded-2xl rounded-tl-sm px-4 py-3">
                <div className="flex gap-1.5">
                  <div className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input Area */}
      <div className="p-3 border-t border-border/20 bg-muted/30">
        {/* Include code toggle */}
        {currentCode && (
          <div className="flex items-center gap-2 mb-2">
            <Button
              type="button"
              variant={includeCode ? "default" : "outline"}
              size="sm"
              onClick={() => setIncludeCode(!includeCode)}
              className={`text-xs h-7 ${includeCode ? 'bg-primary/20 text-primary border-primary/30' : ''}`}
            >
              <Code2 className="h-3 w-3 ml-1" />
              {includeCode ? 'הקוד יישלח' : 'שלח עם הקוד'}
            </Button>
            {includeCode && (
              <span className="text-xs text-muted-foreground">הקוד הנוכחי יצורף להודעה</span>
            )}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="flex gap-2">
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="שאל אותי משהו... 💬"
            disabled={isLoading}
            className="flex-1 bg-background border-border/50 focus:border-primary"
            dir="auto"
          />
          <Button
            type="submit"
            disabled={!input.trim() || isLoading}
            className="bg-primary hover:bg-primary/90 text-primary-foreground px-4 rounded-full"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4 rtl-flip" />
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};
